// NEW_FILE_JUSTIFIED: React-PDF Document component for 4iCover certificate generation
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import type { IFourICoverCertificateProps } from '@/@types/certificate';

const TEAL = '#2A7B6F';
const GOLD = '#B8860B';
const DARK = '#333333';
const LIGHT_GRAY = '#F5F5F5';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', color: DARK },
  header: {
    backgroundColor: TEAL,
    padding: 20,
    marginBottom: 20,
    marginHorizontal: -40,
    marginTop: -40,
  },
  headerTitle: { color: 'white', fontSize: 24, fontFamily: 'Helvetica-Bold' },
  headerSubtitle: { color: 'white', fontSize: 14, marginTop: 4 },
  title: { fontSize: 18, fontFamily: 'Helvetica-Bold', color: DARK, marginBottom: 8 },
  subtitle: { fontSize: 9, color: '#666', marginBottom: 16 },
  sectionTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: TEAL,
    marginBottom: 8,
    marginTop: 16,
  },
  table: { marginBottom: 16 },
  tableHeader: {
    backgroundColor: LIGHT_GRAY,
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: '#DDD',
    padding: 6,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: '#EEE',
    padding: 6,
  },
  tableLabel: { color: GOLD, fontSize: 9, fontFamily: 'Helvetica-Bold' },
  tableValue: { fontSize: 10 },
  twoCol: { flexDirection: 'row' },
  col50: { width: '50%', paddingVertical: 4 },
  fourCol: { width: '25%' },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: TEAL,
    padding: 10,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  footerText: { color: 'white', fontSize: 9 },
  coverBox: { borderWidth: 1, borderColor: '#DDD', padding: 12, marginBottom: 12 },
  contactBox: { borderWidth: 1, borderColor: '#DDD', padding: 16, marginBottom: 16 },
});

export function FourICoverCertificate({ data }: IFourICoverCertificateProps) {
  const {
    policyholder,
    principalMember,
    insuredMembers,
    coverSummary,
    administratorInfo,
    branding,
  } = data;

  return (
    <Document>
      {/* Page 1 */}
      <Page size="A4" style={styles.page}>
        <View style={[styles.header, { backgroundColor: branding.primaryColor }]}>
          <Text style={styles.headerTitle}>{branding.companyName}</Text>
          <Text style={styles.headerSubtitle}>{branding.planLabel}</Text>
        </View>

        <Text style={styles.title}>Certificate of Membership</Text>
        <Text style={styles.subtitle}>
          This document sets out the coverage and benefit details for you and any eligible
          dependants under your plan.
        </Text>

        <Text style={styles.sectionTitle}>Policyholder details</Text>
        <View style={styles.table}>
          <View style={styles.twoCol}>
            <View style={styles.col50}>
              <Text style={styles.tableLabel}>Policyholder Name</Text>
              <Text style={styles.tableValue}>{policyholder.name}</Text>
            </View>
            <View style={styles.col50}>
              <Text style={styles.tableLabel}>Start Date</Text>
              <Text style={styles.tableValue}>{policyholder.startDate}</Text>
            </View>
          </View>
          <View style={styles.twoCol}>
            <View style={styles.col50}>
              <Text style={styles.tableLabel}>Policy Number</Text>
              <Text style={styles.tableValue}>{policyholder.policyNumber}</Text>
            </View>
            <View style={styles.col50}>
              <Text style={styles.tableLabel}>Policy Expiry Date</Text>
              <Text style={styles.tableValue}>{policyholder.expiryDate}</Text>
            </View>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.twoCol}>
            <View style={styles.col50}>
              <Text style={styles.tableLabel}>Principle Member</Text>
              <Text style={styles.tableValue}>{principalMember.name}</Text>
            </View>
            <View style={styles.col50}>
              <Text style={styles.tableLabel}>Date of Birth</Text>
              <Text style={styles.tableValue}>{principalMember.dateOfBirth}</Text>
            </View>
          </View>
          <View style={styles.twoCol}>
            <View style={styles.col50}>
              <Text style={styles.tableLabel}>Member ID</Text>
              <Text style={styles.tableValue}>{principalMember.memberId}</Text>
            </View>
            <View style={styles.col50}>
              <Text style={styles.tableLabel}>Country of Residence</Text>
              <Text style={styles.tableValue}>{principalMember.countryOfResidence}</Text>
            </View>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Insured Members</Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableLabel, styles.fourCol]}>Name</Text>
            <Text style={[styles.tableLabel, styles.fourCol]}>Date of Birth</Text>
            <Text style={[styles.tableLabel, styles.fourCol]}>Start Date</Text>
            <Text style={[styles.tableLabel, styles.fourCol]}>Member ID</Text>
          </View>
          {insuredMembers.map((member, i) => (
            <View key={i} style={styles.tableRow}>
              <Text style={[styles.tableValue, styles.fourCol]}>{member.name}</Text>
              <Text style={[styles.tableValue, styles.fourCol]}>{member.dateOfBirth}</Text>
              <Text style={[styles.tableValue, styles.fourCol]}>{member.startDate}</Text>
              <Text style={[styles.tableValue, styles.fourCol]}>{member.memberId}</Text>
            </View>
          ))}
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>{branding.website}</Text>
        </View>
      </Page>

      {/* Page 2 */}
      <Page size="A4" style={styles.page}>
        <View style={styles.contactBox}>
          <Text style={[styles.sectionTitle, { marginTop: 0 }]}>
            Plan Administrator and Concierge
          </Text>
          <Text style={{ fontSize: 9, marginBottom: 8 }}>{administratorInfo.description}</Text>
          <Text style={{ fontSize: 10 }}>Phone: {administratorInfo.phone}</Text>
          <Text style={{ fontSize: 10 }}>Email: {administratorInfo.email}</Text>
        </View>

        <Text style={styles.sectionTitle}>Cover Summary</Text>
        <View style={styles.coverBox}>
          <View style={styles.twoCol}>
            <View style={styles.col50}>
              <Text style={styles.tableLabel}>Area of Cover</Text>
              <Text style={styles.tableValue}>{coverSummary.areaOfCoverage}</Text>
            </View>
            <View style={styles.col50}>
              <Text style={styles.tableLabel}>Deductible</Text>
              <Text style={styles.tableValue}>{coverSummary.deductible}</Text>
            </View>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Key Benefits</Text>
        <View style={styles.coverBox}>
          <Text style={{ fontSize: 10 }}>{coverSummary.keyBenefitsText}</Text>
        </View>

        <Text style={styles.sectionTitle}>Underwriting Terms and Notes</Text>
        <View style={styles.coverBox}>
          <Text style={{ fontSize: 10 }}>{coverSummary.underwritingNotes || 'None'}</Text>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>{branding.website}</Text>
        </View>
      </Page>
    </Document>
  );
}
